/**
 * Sources UX helpers (21-10).
 *
 * The Sources panel and the answer chip share one source list per assistant
 * message. The list order defines citation numbering: [1] ↔ sources[0] —
 * the SAME order mapCitationTokens (lib/citations.ts) uses, so chip count,
 * panel rows and in-text markers can never desynchronize.
 */
import type { SourceItem } from "@/components/ChatThread";

/** Deduplicate by sourceId (preferred) or url, preserving order. */
export function dedupeSources(sources: SourceItem[] | undefined | null): SourceItem[] {
  if (!sources || sources.length === 0) return [];
  const seen = new Set<string>();
  const out: SourceItem[] = [];
  for (const s of sources) {
    const key = s.sourceId || s.url || s.domain || s.title;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/** 1-based citation index for a source in the deduped list (0 = not found). */
export function sourceIndexAt(sources: SourceItem[], index: number): number {
  return index + 1;
}

/** RU plural for «источник» (same rules as the composer chip). */
export function pluralSources(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "источник";
  if ([2, 3, 4].includes(n % 10) && ![12, 13, 14].includes(n % 100)) return "источника";
  return "источников";
}

/**
 * Format a provider-supplied ISO date (YYYY-MM-DD) for display.
 * Returns null for missing/invalid values — never a fabricated date.
 */
export function formatSourceDate(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Domain for display: backend `domain` first, else URL host, else label. */
export function sourceDomainLabel(s: SourceItem): string {
  if (s.domain) return s.domain;
  if (s.url) {
    try {
      return new URL(s.url).hostname;
    } catch {
      /* fall through */
    }
  }
  return "источник";
}
